import React, { useMemo, useState } from 'react';
import { useScheduleActionLock } from '../../hook/useScheduleActionLock';
import dayjs from 'dayjs';
import {
    AlertCircle,
    Ban,
    CheckCircle2,
    Clock3,
    RotateCcw,
    UserCheck,
    UserX,
    XCircle,
} from 'lucide-react';
import Modal from '../modal/Modal';
import { Col, Row } from '../../lib/utils';
import { MemberScheduleDataType } from '../../services/useMembershipQueryService';
import type { BookingStatus } from '@/features/groupBooking/types';
import { useCancelReservation, useUpdateBookingStatus } from '@/features/groupBooking/hooks';

type EditableBookingStatus = 'CONFIRMED' | 'COMPLETED' | 'NOSHOW';

type Props = {
    centerId: string;
    row: MemberScheduleDataType;
    memberName?: string;
    onClose: () => void;
    onChanged?: () => void;
    createAlert: any;
};

const STATUS_CONFIG: Record<BookingStatus, { label: string; bg: string; color: string; icon: React.ReactNode }> = {
    CONFIRMED: { label: '예약', bg: '#EFF6FF', color: '#1D4ED8', icon: <Clock3 size={14} /> },
    WAITLISTED: { label: '대기', bg: '#FEF3C7', color: '#92400E', icon: <Clock3 size={14} /> },
    COMPLETED: { label: '출석', bg: '#D1FAE5', color: '#065F46', icon: <CheckCircle2 size={14} /> },
    NOSHOW: { label: '노쇼', bg: '#FEE2E2', color: '#991B1B', icon: <UserX size={14} /> },
    CANCELLED: { label: '취소', bg: '#F3F4F6', color: '#6B7280', icon: <Ban size={14} /> },
};

const STATUS_ACTIONS: Record<
    EditableBookingStatus,
    { label: string; description: string; tone: 'blue' | 'green' | 'red'; icon: React.ReactNode }
> = {
    CONFIRMED: {
        label: '예약으로 되돌리기',
        description: '출석/노쇼 보정을 취소하고 예약 상태로 되돌립니다.',
        tone: 'blue',
        icon: <RotateCcw size={16} />,
    },
    COMPLETED: {
        label: '출석 처리',
        description: '회원이 수업에 참여한 상태로 표시합니다.',
        tone: 'green',
        icon: <UserCheck size={16} />,
    },
    NOSHOW: {
        label: '노쇼 처리',
        description: '노쇼 정책에 따라 추가 패널티 회차가 적용될 수 있습니다.',
        tone: 'red',
        icon: <UserX size={16} />,
    },
};

const getStatusActions = (status?: BookingStatus): EditableBookingStatus[] => {
    if (status === 'CONFIRMED') return ['COMPLETED', 'NOSHOW'];
    if (status === 'COMPLETED') return ['CONFIRMED', 'NOSHOW'];
    if (status === 'NOSHOW') return ['CONFIRMED', 'COMPLETED'];
    return [];
};

const getStatusGuide = (status?: BookingStatus) => {
    if (status === 'CONFIRMED') {
        return '예약 상태에서 출석 또는 노쇼로 처리할 수 있습니다.';
    }
    if (status === 'COMPLETED') {
        return '출석 기록을 예약이나 노쇼로 보정할 수 있습니다.';
    }
    if (status === 'NOSHOW') {
        return '노쇼를 다른 상태로 바꾸면 서버가 노쇼 패널티 회차를 복원합니다.';
    }
    return '';
};

const ModalGroupBookingHistoryAction = ({
    centerId,
    row,
    memberName,
    onClose,
    onChanged,
    createAlert,
}: Props) => {
    const [penaltyExempt, setPenaltyExempt] = useState(true);
    /* 스케줄 동작 잠금(설계 §6): 그룹 예약의 출석 상태 변경·예약 취소 = 기존 수업 회원 구성 변경 = 수정 */
    const editLock = useScheduleActionLock('schedule.edit');
    const cancelMutation = useCancelReservation();
    const updateStatusMutation = useUpdateBookingStatus();
    const status = row.bookingStatus as BookingStatus | undefined;
    const statusConfig = status ? STATUS_CONFIG[status] : undefined;
    const statusActions = useMemo(() => getStatusActions(status), [status]);
    const canCancel = status === 'CONFIRMED' || status === 'WAITLISTED';
    const hasRequiredIds = !!centerId && !!row._id && !!row.scheduleId && !!row.memberId;
    const isSubmitting = cancelMutation.isLoading || updateStatusMutation.isLoading;

    const handleStatusChange = (newStatus: EditableBookingStatus) => {
        if (!hasRequiredIds) {
            createAlert('처리 불가', '예약 또는 수업 정보가 부족해 상태를 수정할 수 없습니다.', true);
            return;
        }

        editLock.run(() => updateStatusMutation.mutate(
            {
                centerId,
                bookingId: row._id,
                body: { status: newStatus },
                sessionId: row.scheduleId!,
            },
            {
                onSuccess: () => {
                    createAlert(
                        '상태 수정 완료',
                        `${memberName || row.memberName || '회원'}님의 기록을 ${STATUS_CONFIG[newStatus].label} 상태로 변경했습니다.`,
                        false
                    );
                    onChanged?.();
                    onClose();
                },
                onError: (err: any) => {
                    createAlert('상태 수정 실패', err?.serverMessage || err?.message || '상태 수정에 실패했습니다.', true);
                },
            }
        ));
    };

    const handleCancel = () => {
        if (!hasRequiredIds) {
            createAlert('처리 불가', '예약 또는 수업 정보가 부족해 취소할 수 없습니다.', true);
            return;
        }

        editLock.run(() => cancelMutation.mutate(
            {
                centerId,
                sessionId: row.scheduleId!,
                memberId: row.memberId,
                params:
                    status === 'CONFIRMED'
                        ? { cancelledBy: 'admin', penaltyExempt: penaltyExempt ? 'true' : 'false' }
                        : { cancelledBy: 'admin' },
            },
            {
                onSuccess: (result) => {
                    const promoted = result?.data?.promoted;
                    const baseMessage =
                        status === 'WAITLISTED'
                            ? `${memberName || row.memberName || '회원'}님의 대기 예약을 취소했습니다.`
                            : `${memberName || row.memberName || '회원'}님의 예약을 취소했습니다.`;
                    const promotedMessage = promoted?.memberName
                        ? ` ${promoted.memberName}님이 대기에서 예약으로 승격되었습니다.`
                        : '';
                    createAlert('취소 완료', `${baseMessage}${promotedMessage}`, false);
                    onChanged?.();
                    onClose();
                },
                onError: (err: any) => {
                    createAlert('취소 실패', err?.serverMessage || err?.message || '예약 취소에 실패했습니다.', true);
                },
            }
        ));
    };

    return (
        <Modal isCloseButton onClose={onClose} whiteViewWidth={560} mt={80} mb={80}>
            <Col style={{ padding: '32px 32px 28px', gap: 20 }}>
                <Col style={{ gap: 8 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.25 }}>
                        회차 기록 관리
                    </span>
                    <span style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
                        예약 취소와 출석 상태 보정을 이 기록에서 바로 처리합니다.
                    </span>
                </Col>

                <Col
                    style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: 12,
                        backgroundColor: '#F9FAFB',
                        padding: 16,
                        gap: 12,
                    }}
                >
                    <Row style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                        <Col style={{ gap: 6, minWidth: 0 }}>
                            <span style={{ fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1.35 }}>
                                {row.sessionTitle || '그룹수업'}
                            </span>
                            <span style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5 }}>
                                {dayjs(row.startTime).format('YYYY.MM.DD HH:mm')} ~ {dayjs(row.endTime).format('HH:mm')}
                            </span>
                            <span style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                                {row.coachName ? `${row.coachName} 강사` : '강사 정보 없음'}
                            </span>
                        </Col>
                        {statusConfig && (
                            <Row
                                style={{
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '6px 10px',
                                    borderRadius: 999,
                                    backgroundColor: statusConfig.bg,
                                    color: statusConfig.color,
                                    fontSize: 12,
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {statusConfig.icon}
                                {statusConfig.label}
                            </Row>
                        )}
                    </Row>
                    <Row
                        style={{
                            justifyContent: 'space-between',
                            gap: 12,
                            borderTop: '1px solid #E5E7EB',
                            paddingTop: 12,
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#6B7280' }}>회차 영향</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', textAlign: 'right' }}>
                            {row.countImpactLabel || '영향 정보 없음'}
                        </span>
                    </Row>
                </Col>

                {!hasRequiredIds && (
                    <InfoBox
                        tone="red"
                        text="예약 또는 수업 식별자가 없어 이 기록은 수정할 수 없습니다. 최신 데이터로 새로고침한 뒤 다시 시도해주세요."
                    />
                )}

                {statusActions.length > 0 && (
                    <Col style={{ gap: 12 }}>
                        <SectionTitle title="상태 수정" description={getStatusGuide(status)} />
                        <Col style={{ gap: 10 }}>
                            {statusActions.map((nextStatus) => {
                                const action = STATUS_ACTIONS[nextStatus];
                                return (
                                    <ActionButton
                                        key={nextStatus}
                                        label={action.label}
                                        description={action.description}
                                        tone={action.tone}
                                        icon={action.icon}
                                        disabled={isSubmitting || !hasRequiredIds}
                                        onClick={() => handleStatusChange(nextStatus)}
                                    />
                                );
                            })}
                        </Col>
                    </Col>
                )}

                {canCancel && (
                    <Col style={{ gap: 12 }}>
                        <SectionTitle
                            title={status === 'WAITLISTED' ? '대기 취소' : '예약 취소'}
                            description={
                                status === 'WAITLISTED'
                                    ? '대기 상태는 회차 차감이 없으므로 취소해도 회차가 변하지 않습니다.'
                                    : '예약 취소 시 수업의 취소 마감 정책에 따라 회차 복원 여부가 결정됩니다.'
                            }
                        />
                        {status === 'CONFIRMED' && (
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 10,
                                    padding: 12,
                                    borderRadius: 10,
                                    backgroundColor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    cursor: 'pointer',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={penaltyExempt}
                                    onChange={(event) => setPenaltyExempt(event.target.checked)}
                                    style={{ width: 16, height: 16, marginTop: 2, accentColor: '#059669', cursor: 'pointer' }}
                                />
                                <Col style={{ gap: 4 }}>
                                    <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>
                                        회차 복원 처리
                                    </span>
                                    <span style={{ fontSize: 12, color: '#64748B', lineHeight: 1.45 }}>
                                        체크하면 취소 마감 이후라도 패널티 면제로 기록하고 회차를 복원합니다.
                                    </span>
                                </Col>
                            </label>
                        )}
                        <ActionButton
                            label={status === 'WAITLISTED' ? '대기 취소하기' : '예약 취소하기'}
                            description={
                                status === 'WAITLISTED'
                                    ? '대기열에서 제거합니다.'
                                    : '취소 후 빈 자리가 생기면 기존 대기자가 자동 승격될 수 있습니다.'
                            }
                            tone="red"
                            icon={<XCircle size={16} />}
                            disabled={isSubmitting || !hasRequiredIds}
                            onClick={handleCancel}
                        />
                    </Col>
                )}

                {(status === 'COMPLETED' || status === 'NOSHOW') && (
                    <InfoBox
                        tone="amber"
                        text="출석/노쇼 기록은 바로 취소할 수 없습니다. 취소가 필요하면 먼저 예약으로 되돌린 뒤 다시 이 기록을 열어 예약 취소를 진행해주세요."
                    />
                )}

                {status === 'CANCELLED' && (
                    <InfoBox
                        tone="gray"
                        text="취소된 기록은 이력 보존 대상이라 수정하지 않습니다. 다시 등록이 필요하면 상단의 수업 등록에서 새 예약을 추가해주세요."
                    />
                )}

                <Row style={{ justifyContent: 'flex-end', paddingTop: 4 }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            height: 40,
                            minWidth: 96,
                            borderRadius: 8,
                            border: '1px solid #D1D5DB',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        닫기
                    </button>
                </Row>
            </Col>
            {editLock.gate}
        </Modal>
    );
};

const SectionTitle = ({ title, description }: { title: string; description: string }) => (
    <Col style={{ gap: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{title}</span>
        <span style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.45 }}>{description}</span>
    </Col>
);

const InfoBox = ({ tone, text }: { tone: 'red' | 'amber' | 'gray'; text: string }) => {
    const toneStyle = {
        red: { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
        amber: { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E' },
        gray: { bg: '#F8FAFC', border: '#E2E8F0', color: '#475569' },
    }[tone];

    return (
        <Row
            style={{
                gap: 8,
                alignItems: 'flex-start',
                padding: 12,
                borderRadius: 10,
                backgroundColor: toneStyle.bg,
                border: `1px solid ${toneStyle.border}`,
            }}
        >
            <AlertCircle size={16} color={toneStyle.color} style={{ marginTop: 1, flexShrink: 0 }} />
            <span style={{ fontSize: 12, lineHeight: 1.55, color: toneStyle.color, fontWeight: 600 }}>{text}</span>
        </Row>
    );
};

const ActionButton = ({
    label,
    description,
    tone,
    icon,
    disabled,
    onClick,
}: {
    label: string;
    description: string;
    tone: 'blue' | 'green' | 'red';
    icon: React.ReactNode;
    disabled?: boolean;
    onClick: () => void;
}) => {
    const [isHover, setIsHover] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const toneStyle = {
        blue: { bg: '#EFF6FF', hover: '#DBEAFE', active: '#BFDBFE', border: '#BFDBFE', color: '#1D4ED8' },
        green: { bg: '#ECFDF5', hover: '#D1FAE5', active: '#A7F3D0', border: '#A7F3D0', color: '#047857' },
        red: { bg: '#FEF2F2', hover: '#FEE2E2', active: '#FECACA', border: '#FECACA', color: '#B91C1C' },
    }[tone];

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => {
                setIsHover(false);
                setIsActive(false);
            }}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            style={{
                width: '100%',
                minHeight: 58,
                borderRadius: 10,
                border: `1px solid ${toneStyle.border}`,
                backgroundColor: disabled ? '#F3F4F6' : isActive ? toneStyle.active : isHover ? toneStyle.hover : toneStyle.bg,
                color: disabled ? '#9CA3AF' : toneStyle.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 12px',
                textAlign: 'left',
                cursor: disabled ? 'not-allowed' : 'pointer',
                boxShadow: isHover && !disabled ? '0 8px 18px rgba(15, 23, 42, 0.08)' : 'none',
                transform: isHover && !isActive && !disabled ? 'translateY(-1px)' : 'translateY(0)',
                transition:
                    'background-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, color 0.16s ease',
            }}
        >
            <Row style={{ alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span style={{ flexShrink: 0, display: 'flex' }}>{icon}</span>
                <Col style={{ gap: 3, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.35 }}>{label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45, color: disabled ? '#9CA3AF' : '#64748B' }}>
                        {description}
                    </span>
                </Col>
            </Row>
        </button>
    );
};

export default ModalGroupBookingHistoryAction;
